import { Form } from 'antd';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthenticateByPasswordMutation } from '@app/api/Authentication';
import { ACCESS_RIGHTS_PROP, AUTHORIZATION_PATH } from '@app/constants';
import { handleCatchError } from '@app/services';
import {
  setAccessRights,
  setOrganizations,
  setUser,
  useAppDispatch,
} from '@app/store';

import { useAuthorizationPreparedForm } from '@pages/Authorization/hooks/useAuthorizationPreparedForm';

import type { PasswordFormValues } from '@pages/Authorization/Authorization.types';
import { adaptUserToRedux } from '@pages/Authorization/utils/adaptUserToRedux';
import {
  persistAuthorizationAccessToken,
  persistAuthorizationCookies,
} from '@pages/Authorization/utils/authorizationSession';

interface UseAuthorizationPasswordProps {
  email: string;
  isEnabled: boolean;
  setEmail: (email: string) => void;
}

export const useAuthorizationPassword = ({
  email,
  isEnabled,
  setEmail,
}: UseAuthorizationPasswordProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm<PasswordFormValues>();

  const [authenticateByPassword] = useAuthenticateByPasswordMutation();

  const {
    captchaChangeHandler,
    captchaImage,
    clearServerErrorHandler,
    emailChangeHandler,
    isPageLoader,
    isSubmitLoader,
    registerAuthAttemptHandler,
    resetCaptchaFieldHandler,
    serverError,
    setServerError,
    setSubmitLoader,
    validateCaptchaHandler,
  } = useAuthorizationPreparedForm({
    email,
    getEmailFieldValue: () => form.getFieldValue('email') || '',
    isEnabled,
    setCaptchaFieldValue: (value) => form.setFieldValue('captcha', value),
    setEmail,
    setEmailFieldValue: (value) => form.setFieldValue('email', value),
  });

  const finishHandler = useCallback(
    async (values: PasswordFormValues) => {
      const isCaptchaValid = await validateCaptchaHandler();

      if (!isCaptchaValid) {
        return;
      }

      setSubmitLoader(true);

      const { email, password } = values;

      try {
        const result = await authenticateByPassword({
          Email: email,
          Password: password,
          Phone: null,
        }).unwrap();

        dispatch(setUser(adaptUserToRedux(result)));
        dispatch(setAccessRights(result[ACCESS_RIGHTS_PROP]));
        dispatch(setOrganizations(result.Organizations));
        persistAuthorizationCookies(result);
        persistAuthorizationAccessToken(result.AccessToken);
        navigate('/');
      } catch (error) {
        setServerError(handleCatchError(error));
        navigate(AUTHORIZATION_PATH);
      } finally {
        registerAuthAttemptHandler();
        resetCaptchaFieldHandler();
        setSubmitLoader(false);
      }
    },
    [
      authenticateByPassword,
      dispatch,
      navigate,
      registerAuthAttemptHandler,
      resetCaptchaFieldHandler,
      setServerError,
      setSubmitLoader,
      validateCaptchaHandler,
    ]
  );

  return {
    captchaChangeHandler,
    captchaImage,
    clearServerErrorHandler,
    emailChangeHandler,
    finishHandler,
    form,
    isPageLoader,
    isSubmitLoader,
    serverError,
  };
};
